<template>
  <div>
    <md-content>
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-35">
          <md-field
            v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot"
            :class="!checkRange($store.state.plot.plot.plot_num, 0, 99999) ? 'md-invalid' : ''">
            <label for="plotNum">{{ $t("plot.plotNum") }}</label>
            <md-input type="number" name="plotNum" v-model="$store.state.plot.plot.plot_num" :class="{'disabled': edit === true}" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-99999)</span>
          </md-field>
          <md-field
            v-else-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.eventPlot"
            :class="!checkRange($store.state.plot.plot.event_num, 0, 99999) ? 'md-invalid' : ''">
            <label for="eventNum">{{ $t("plot.eventNum") }}</label>
            <md-input type="number" name="eventNum" v-model="$store.state.plot.plot.event_num" :class="{'disabled': edit === true}" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-99999)</span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-65" v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot">
          <md-field>
            <select v-model="$store.state.plot.plot.plot_sample" name="plotSample">
              <option disabled value="">{{ $t("plot.plotSample") }}</option>
              <option v-for="(pl, key) in plotSampleList" :value="key" :key="key">{{ pl }}</option>
            </select>
          </md-field>
        </div>

        <div class="md-layout-item md-size-50" v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot">
          <md-field :class="gpsError.length > 0 ? 'md-invalid' : ''">
            <md-icon class="gps-icon" @click.native="getCoords()">location_on</md-icon>
            <span class="gps-text">{{ $store.state.plot.plot.coords.latitude }}, {{ $store.state.plot.plot.coords.longitude }}</span>
            <spinner :load="gpsLoad" />
            <span class="md-error">{{ gpsError }}</span>
          </md-field>
        </div>

        <div class="md-layout-item md-size-20" v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot">
          <md-field :class="!checkRange($store.state.plot.plot.elevation, 0, 2800) ? 'md-invalid' : ''">
            <label for="elevation">{{ $t("plot.elevation") }}</label>
            <md-input type="number" name="elevation" v-model="$store.state.plot.plot.elevation" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-2800)</span>
          </md-field>
        </div>

        <div class="md-layout-item md-size-30" v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot">
          <md-field>
            <select v-model="$store.state.plot.plot.slope" name="slope">
              <option disabled value="">{{ $t("plot.slope") }}</option>
              <option v-for="(value, slope) in areas" :value="slope" :key="slope">{{ slope }} %</option>
            </select>
          </md-field>
        </div>

        <div class="md-layout-item md-size-100" :class="{'disabled': edit === true}">
          <div v-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.forestPlot">
            <type-button @setType="setType" plotType="plot_type" type="forest"></type-button>
            <type-button @setType="setType" plotType="plot_type" type="pastures"></type-button>
            <type-button @setType="setType" plotType="plot_type" type="shrubs"></type-button>
            <type-button @setType="setType" plotType="plot_type" type="nonwood"></type-button>
          </div>
          <div v-else-if="$store.state.plot.localization.plotType === $store.state.plotTypeLoc.eventPlot">
            <div class="md-layout-item md-size-100">
              <type-button @setType="setEvent" plotType="event_type" type="fire"></type-button>
              <type-button @setType="setEvent" plotType="event_type" type="erosion"></type-button>
              <type-button @setType="setEvent" plotType="event_type" type="other"></type-button>
              <type-button @setType="setEvent" plotType="event_type" type="illegal_cut"></type-button>
            </div>
          </div>
        </div>

        <div class="md-layout-item md-size-100">
          <md-field>
            <label for="note">{{ $t("plot.note") }}</label>
            <md-input type="textarea" name="note" v-model="$store.state.plot.plot.note" />
          </md-field>
        </div>
      </div>
    </md-content>

    <buttons-nav :prev="prev" next step="3" />

    <error-dialog :show="error" :text="errorText" @closeError="error = false" />
  </div>
</template>

<script>
import ButtonsNav from '@/components/ButtonsNav'
import Spinner from '@/components/Spinner'
import ErrorDialog from '@/components/ErrorDialog'
import TypeButton from '@/components/TypeButton'

import { getDomain } from '@/assets/scripts/domainsManager'
import getCoords from '@/assets/scripts/getCoords'
import { checkRange, normalize } from '@/assets/scripts/formatData'

const AREA = 'plot_radius'
const PLOT_LOC = 'plot_sample'

export default {
  props: ['measurements'],
  components: {
    ButtonsNav,
    Spinner,
    ErrorDialog,
    TypeButton
  },
  data: () => ({
    plotSampleList: {},
    areas: {},
    gpsError: '',
    gpsLoad: false,
    error: false,
    errorText: '',
    edit: false,
    prev: 'Localization',
    next: 'PlotType',
    checkRange: checkRange,
    gpsClicked: false
  }),
  mounted () {
    // save and go to next page
    this.$on('next', this.checkPlotNum)

    this.setLists()
    this.$store.state.plot.id === null ? this.edit = false : this.edit = true

    if (this.edit) { // edit
      this.$store.state.plot.localization.plotType === '01'
        ? this.$emit('title', this.$i18n.t('page.plot') + ' (' + this.$i18n.t('global.edit') + ')')
        : this.$emit('title', this.$i18n.t('page.event') + ' (' + this.$i18n.t('global.edit') + ')')
    } else { // new
      this.$store.state.plot.localization.plotType === '01'
        ? this.$emit('title', this.$i18n.t('page.plot') + ' (' + this.$i18n.t('global.new') + ')')
        : this.$emit('title', this.$i18n.t('page.event') + ' (' + this.$i18n.t('global.new') + ')')
      this.$store.dispatch('plot/getTmp', 'plot')
      if (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.forestPlot) {
        if (this.$store.state.plot.plot.plot_sample === '') this.$store.state.plot.plot.plot_sample = 'P'
        if (this.$store.state.plot.plot.slope === '') this.$store.state.plot.plot.slope = '0'
      }
    }
  },
  methods: {
    save: function () {
      if ((this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.forestPlot &&
          checkRange(this.$store.state.plot.plot.elevation, 0, 2800) &&
          checkRange(this.$store.state.plot.plot.plot_num, 0, 99999)) ||
          (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.eventPlot &&
          checkRange(this.$store.state.plot.plot.event_num, 0, 99999))) {
        // normalize value => remove comma or 0 before number
        this.$store.state.plot.plot.plot_num = normalize(this.$store.state.plot.plot.plot_num, 0)
        this.$store.state.plot.plot.event_num = normalize(this.$store.state.plot.plot.event_num, 0)
        if (!this.edit) this.$store.dispatch('plot/setTmp', 'plot') // save in tmp for the next step
        this.$router.push(this.next)
      } else {
        this.error = true
        this.errorText = this.$i18n.t('error.errorPlotType')
      }
    },

    setLists: function () {
      getDomain(AREA).then(data => {
        this.areas = data
      })
      getDomain(PLOT_LOC).then(data => {
        this.plotSampleList = data[this.$i18n.locale]
      })
    },

    setType: function (type) {
      this.$store.state.plot.plot.plot_type = type
    },

    setEvent: function (event) {
      this.$store.state.plot.plot.event_type = event
    },

    getCoords: function () {
      this.gpsClicked = true
      this.gpsLoad = true
      this.gpsError = ''
      getCoords().then(coords => {
        this.gpsLoad = false
        this.$store.state.plot.plot.coords.latitude = coords.latitude
        this.$store.state.plot.plot.coords.longitude = coords.longitude
      }).catch(err => {
        this.gpsLoad = false
        this.gpsError = err
      })
    },

    checkPlotNum: function () {
      if (!this.edit) {
        let check = true
        // check plot number and plot type
        if (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.forestPlot) {
          check = this.plotRequiredField(this.$store.state.plot.plot.plot_num) && this.plotRequiredField(this.$store.state.plot.plot.plot_type)
        // check event number and event type
        } else if (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.eventPlot) {
          check = this.plotRequiredField(this.$store.state.plot.plot.event_num) && this.plotRequiredField(this.$store.state.plot.plot.event_type)
        } else {
          check = false
        }
        if (check && this.checkGps()) {
          this.measurements.iterate((value, key, iterationNumber) => {
            // check if plot number already exist (if is not undefined => we are in plot type)
            if (this.$store.state.plot.plot.plot_num !== undefined && parseInt(value.plot.plot_num) === parseInt(this.$store.state.plot.plot.plot_num)) {
              check = false
            }
            // check if event number already exist (if is not undefined => we are in event type)
            if (this.$store.state.plot.plot.event_num !== undefined && parseInt(value.plot.event_num) === parseInt(this.$store.state.plot.plot.event_num)) {
              check = false
            }
          }).then(() => {
            if (check) {
              this.save()
            } else {
              this.error = true
              if (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.forestPlot) {
                this.errorText = this.$i18n.t('error.errorPlotNum')
              } else {
                this.errorText = this.$i18n.t('error.errorEventNum')
              }
            }
          }).catch(err => {
            console.log(err)
          })
        } else {
          this.error = true
          if (this.$store.state.plot.localization.plotType === this.$store.state.plotTypeLoc.forestPlot) {
            this.errorText = this.$i18n.t('error.errorPlotType')
          } else {
            this.errorText = this.$i18n.t('error.errorEventType')
          }
        }
      } else {
        this.save()
      }
    },

    plotRequiredField: function (number) {
      return !(number === undefined || number === null || number === '')
    },

    checkGps: function () {
      if (this.$store.state.plot.localization.plotType === '01') {
        return this.gpsClicked || (this.$store.state.plot.plot.coords.latitude !== 0 && this.$store.state.plot.plot.coords.longitude !== 0)
      } else {
        return true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';

  .gps-icon {
    margin-right: 25px !important;
    margin-left: 0 !important;
  }

  .btn-type {
    width: 68px;
    height: 68px;
  }

  .disabled {
    user-select: none;
    pointer-events: none;
  }
</style>
